import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import type { CasinoConfigGameRow } from "../../../utils/game-utils/gamesUtils";
import { mapAdminTreeGameToRow } from "./mapAdminTreeGameToRow";

export const CASINO_GAMES_TREE_PAGE_SIZE = 50;

export type CasinoGamesTreeGroupPageMeta = {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
};

export type CasinoGamesTreeGroupPage = {
  provider: string;
  sub: string;
  total: number;
  active: number;
  items: CasinoConfigGameRow[];
  meta: CasinoGamesTreeGroupPageMeta;
};

export async function fetchCasinoGamesTreeGroupPage(
  provider: string,
  sub: string,
  page: number,
  search = "",
): Promise<CasinoGamesTreeGroupPage> {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  const instance = createPrivateAdminPanelInstance(token?.AccessToken);
  const path = `${API_ROUTES.GET_ADMIN_GAMES_TREE}/${encodeURIComponent(provider)}/${encodeURIComponent(sub)}`;

  const params: { page: number; page_size: number; search?: string } = {
    page,
    page_size: CASINO_GAMES_TREE_PAGE_SIZE,
  };
  const trimmedSearch = search.trim();
  if (trimmedSearch) params.search = trimmedSearch;

  const { data: responseBody } = await instance.get(path, {
    params,
  });
  if (responseBody?.success === false) {
    throw new Error(responseBody?.message || "Failed to fetch games group");
  }
  const payload = responseBody?.data;
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid games group response");
  }

  const rawItems = (payload as { items?: unknown[] }).items;
  const items = Array.isArray(rawItems)
    ? rawItems.map((row) => mapAdminTreeGameToRow(row as Record<string, unknown>))
    : [];
  const meta = (payload as { meta?: CasinoGamesTreeGroupPageMeta }).meta ?? {
    page,
    page_size: CASINO_GAMES_TREE_PAGE_SIZE,
    total: items.length,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  };

  return {
    provider: String((payload as { provider?: unknown }).provider ?? provider),
    sub: String((payload as { sub?: unknown }).sub ?? sub),
    total: Number((payload as { total?: unknown }).total ?? items.length),
    active: Number((payload as { active?: unknown }).active ?? 0),
    items,
    meta,
  };
}

export async function fetchAllCasinoGamesTreeGroupPages(
  provider: string,
  sub: string,
  search = "",
): Promise<CasinoConfigGameRow[]> {
  const out: CasinoConfigGameRow[] = [];
  let page = 1;
  let hasNext = true;

  const MAX_PAGES = 200;
  while (hasNext && page <= MAX_PAGES) {
    const chunk = await fetchCasinoGamesTreeGroupPage(provider, sub, page, search);
    out.push(...chunk.items);
    hasNext = Boolean(chunk.meta?.has_next);
    page += 1;
  }
  return out;
}
